import rtkApis from '@rtkApis';
import tenantTransformers from '@transformers';
import { setAppUser, setMemberArea, setMultiPlatform } from '../store/appSlice';
import parentApi from '../store/parentApi';
import { mapUserFromIdToken } from '../utility/user';

const userApis = parentApi.injectEndpoints({
  endpoints: (build) => ({
    getUserDetailFromToken: build.query({
      query: () => ({ url: `/api/surge/users/current`, method: 'GET' }),
      providesTags: ['current'],
      transformResponse: (response) => tenantTransformers.userDetailTransformer(response),
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        try {
          const response = await queryFulfilled;
          const idToken = getState().auth.login.idToken;
          const jwtFlags = idToken ? mapUserFromIdToken(idToken) : {};
          const isMemberArea = !response?.data?.is_package_user;
          dispatch(setAppUser({ ...response?.data, is_host_user: jwtFlags.is_host_user }));
          dispatch(setMemberArea(isMemberArea));
          dispatch(setMultiPlatform(!!response?.data?.is_multi_platform));
        } catch (e) {
          console.error('Failed to update Login User', e);
        }
      },
    }),

    getCurrentUser: build.query({
      query: () => ({ url: `/api/surge/users/current`, method: 'GET' }),
      transformResponse: (response) => tenantTransformers.userDetailTransformer(response),
    }),

    getProfileDetails: build.query({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        if (rtkApis.getProfileDetailsEndpoint?.queryFn) {
          return rtkApis.getProfileDetailsEndpoint.queryFn(arg, api, extraOptions, baseQuery);
        }
        return { error: 'No queryFn defined for getProfileDetailsEndpoint' };
      },
      providesTags: ['user-profile'],
    }),

    getAutoGeneratedUserContent: build.query({
      query: (body) => rtkApis.getAutoGeneratedUserDescription.query(body),
    }),

    updateProfileDetails: build.mutation({
      query: (data) => rtkApis.updateProfileDetailsEndpoint(data),
      transformResponse: (response) => {
        return rtkApis.updateProfileDetailsEndpoint()?.transformer(response);
      },
      invalidatesTags: (result, error) => {
        return result ? ['user-profile', 'profile', 'current', 'agency-profile'] : [];
      },
    }),

    getFavouriteProperties: build.query({
      query: () => rtkApis.getFavouriteProperties.query(),
      providesTags: ['fav-proprties'],
    }),

    saveSettings: build.mutation({
      query: (params) => rtkApis.saveSettings.query(params),
    }),

    getUserActiveLicenses: build.query({
      query: (params) => rtkApis.getUserActiveLicenses.query(params),
      providesTags: ['licenses'],
    }),

    updateLicense: build.mutation({
      query: (params) => rtkApis.updateLicense.query(params),
      invalidatesTags: (result, error) => {
        return result ? ['licenses'] : [];
      },
    }),

    updateUserPreferences: build.mutation({
      query: (userId) => rtkApis.updateUserPreferences(userId),
      invalidatesTags: ['current', 'profile'],
    }),

    updatePushNotifications: build.mutation({
      query: ({ userId, payload }) => rtkApis.updatePushNotifications({ userId, payload }),
      invalidatesTags: ['current', 'profile'],
    }),

    changePassword: build.mutation({
      query: (query) => rtkApis.changePassword?.query(query),
      transformResponse: (response) => rtkApis.changePassword?.transformer(response),
    }),

    getBankingDetails: build.query({
      query: () => rtkApis.getBankingDetails?.query(),
      transformResponse: (response) => rtkApis.getBankingDetails?.transformer(response),
      providesTags: ['profile'],
    }),

    saveBankDetails: build.mutation({
      query: (params) => rtkApis.saveBankDetails?.query(params),
      transformResponse: (response) => rtkApis.saveBankDetails?.transformer(response),
      invalidatesTags: ['profile'],
    }),

    getBankingDetails: build.query({
      query: () => rtkApis.getBankingDetails?.query(),
      transformResponse: (response) => rtkApis.getBankingDetails?.transformer(response),
      providesTags: ['profile'],
    }),

    saveBankDetails: build.mutation({
      query: (params) => rtkApis.saveBankDetails?.query(params),
      transformResponse: (response) => rtkApis.saveBankDetails?.transformer(response),
      invalidatesTags: ['profile'],
    }),

    getUserSettingsDetail: build.query({
      queryFn: (arg, api, extraOptions, baseQuery) => {
        if (rtkApis.getUserSettingsDetail?.queryFn) {
          return rtkApis.getUserSettingsDetail.queryFn(arg, api, extraOptions, baseQuery);
        }

        const query = rtkApis.getUserSettingsDetail?.query(arg);
        if (!query) {
          return { error: 'No query defined for getUserSettingsDetail' };
        }

        return baseQuery(query).then((response) => {
          if (response.error) {
            return { error: response.error };
          }

          const transformedResponse = rtkApis.getUserSettingsDetail?.transformer
            ? rtkApis.getUserSettingsDetail.transformer(response.data)
            : response.data;
          return { data: transformedResponse };
        });
      },
      providesTags: ['profile'],
    }),

    getUsersSavedSearches: build.query({
      query: (queryParams) => rtkApis.getUsersSavedSearches.query(queryParams),
      providesTags: ['saved-searches'],
    }),

    deleteSavedSearch: build.mutation({
      query: (userId) => rtkApis.deleteSavedSearch(userId),
      invalidatesTags: (result, error) => {
        return result ? ['saved-searches'] : [];
      },
    }),

    getUserBookings: build.query({
      query: (queryParams) => ({
        url: `/bnb/listing_bookings?page=1&per_page=1`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        // Match legionbnb-host guest bookings parsing: support nested `data` envelope and summary fallbacks.
        const root = response?.data ?? response;
        const pagination = root?.pagination ?? root?.meta?.pagination;
        const summary = root?.summary ?? root?.meta?.summary;
        const list = root?.listing_bookings ?? root?.bookings ?? [];
        const fromPagination =
          pagination?.total_count ?? pagination?.total ?? pagination?.total_entries;
        const fromSummary =
          summary?.total_bookings ?? summary?.total ?? summary?.total_count;
        const total =
          fromPagination ??
          fromSummary ??
          (Array.isArray(list) ? list.length : 0);
        return { total: Number(total) || 0 };
      },
      providesTags: ['user-bookings'],
    }),

    getUserOtp: build.query({
      query: (queryParams) => rtkApis.getUserOtp.query(queryParams),
    }),



    verifyUserOtp: build.mutation({
      query: (queryParams) => rtkApis.verifyUserOtp.query(queryParams),
    }),

    fetchContractsDetails: build.query({
      query: (queryParams) => rtkApis.fetchContractsDetails?.query(queryParams),
      async onQueryStarted(arg, api) {
        rtkApis.fetchContractsDetails?.afterSuccess(arg, api);
      },
    }),

    getTruPointsCriteriaData: build.query({
      query: () => rtkApis.getTruPointsCriteriaData.query()?.url,
      transformResponse: (response) => {
        return response;
      },
    }),

    getTruPointsActivityLogsData: build.query({
      query: (query) => rtkApis.getTruPointsActivityLogsData.query(query)?.url,
      transformResponse: (response) => {
        return response;
      },
    }),

    getUserNotificationsData: build.query({
      query: (query) => rtkApis.getUserNotificationsData.query(query)?.url,
      transformResponse: (response) => {
        return response;
      },
      providesTags: ['user-notifications'],
    }),

    getUserNotificationsCount: build.query({
      query: (query) => rtkApis.getUserNotificationsCount.query(query)?.url,
      providesTags: ['user-notifications-count'],
    }),

    markAsRead: build.mutation({
      query: (query) => rtkApis.markAsRead.query(query),
      invalidatesTags: (result, error) => {
        return result ? ['user-notifications', 'user-notifications-count'] : [];
      },
    }),
    markAllAsRead: build.mutation({
      query: (query) => rtkApis.markAllAsRead.query(query),
      invalidatesTags: (result, error) => {
        return result ? ['user-notifications', 'user-notifications-count'] : [];
      },
    }),

    getTruBrokerLeaderboardData: build.query({
      query: (query) => rtkApis.getTruBrokerLeaderboardData.query(query)?.url,
      transformResponse: (response, meta, args) =>
        rtkApis.getTruBrokerLeaderboardData.transformer(response, meta, args),
    }),

    getTeamPerformanceData: build.query({
      query: (params) => ({
        url: `/api/surge/users/team_performance${params ? `?${params}` : ''}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        return response;
      },
    }),

    imageAnalyser: build.query({
      query: (uuid) => ({
        url: `/api/surge/users/analyse_profile_image?uuid=${uuid}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        return response;
      },
    }),
    getNafathVerificationUrl: build.query({
      query: (params) => rtkApis.getNafathVerificationUrl.query(params),
    }),

    getSurgeLanguagesList: build.query({
      query: () => ({
        url: '/api/surge/languages',
        method: 'GET',
      }),
      transformResponse: (response) => tenantTransformers.mapSurgeLanguagesListResponse(response),
    }),

    getSurgeExperienceList: build.query({
      query: () => ({
        url: '/api/surge/experience_list',
        method: 'GET',
      }),
      transformResponse: (response) => tenantTransformers.mapSurgeExperienceListResponse(response),
    }),
  }),
});

export const {
  useGetUserDetailFromTokenQuery,
  useLazyGetFavouritePropertiesQuery,
  useSaveSettingsMutation,
  useLazyGetUserDetailFromTokenQuery,
  useLazyGetCurrentUserQuery,
  useGetUserActiveLicensesQuery,
  useFetchContractsDetailsQuery,
  useLazyGetUsersSavedSearchesQuery,
  useDeleteSavedSearchMutation,
  useLazyGetUserBookingsQuery,
  useGetProfileDetailsQuery,
  useLazyGetUserSettingsDetailQuery,
  useGetUserSettingsDetailQuery,
  useChangePasswordMutation,
  useUpdateProfileDetailsMutation,
  useUpdateUserPreferencesMutation,
  useUpdateLicenseMutation,
  useVerifyUserOtpMutation,
  useLazyGetUserOtpQuery,
  useLazyGetTruPointsCriteriaDataQuery,
  useLazyGetTruPointsActivityLogsDataQuery,
  useLazyGetTruBrokerLeaderboardDataQuery,
  useLazyGetTeamPerformanceDataQuery,
  useUpdatePushNotificationsMutation,
  useLazyGetUserNotificationsDataQuery,
  useLazyGetUserNotificationsCountQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useLazyImageAnalyserQuery,
  useGetNafathVerificationUrlQuery,
  useGetUserNotificationsCountQuery,
  useLazyGetAutoGeneratedUserContentQuery,
  useGetSurgeLanguagesListQuery,
  useGetSurgeExperienceListQuery,
  useGetBankingDetailsQuery,
  useSaveBankDetailsMutation
} = userApis;
